/**
 * Official motorcycle specs (`model-specs.ts`) → label/value text.
 *
 * Client-safe: no data import. The server picks what a model may show
 * (`visibleModelSpecs`) and hands this module the small `ModelSpecFacts`;
 * the product page's vehicle table, its JSON-LD and the model hub all format
 * through here so the three say the same thing.
 *
 * Labels and units are `product.bikeSpecs.*` messages.
 */

import { defaultLocale, locales, type Locale } from "@/i18n/routing";
import type { BikeSpecTranslator } from "./bike-specs";

export type EngineCooling = "liquid" | "air" | "forcedAir" | "oil";
export type EngineLayout =
  | "single"
  | "twin"
  | "inlineTwin"
  | "parallelTwin"
  | "vTwin"
  | "inlineFour"
  | "parallelFour"
  | "vFour"
  | "parallelSix";

/** MT / DCT with gear count; AT = belt CVT (scooters). */
export type Gearbox = { kind: "MT" | "DCT"; gears: number } | { kind: "AT" };

/** What one model may show — `null` fields are not shown. */
export interface ModelSpecFacts {
  displacementCc: number | null;
  engine: { cooling: EngineCooling; layout: EngineLayout } | null;
  gearbox: Gearbox | null;
  /** The four below only for models whose every core field is confirmed. */
  seatHeightMm: number | null;
  curbWeightKg: number | null;
  fuelTankL: number | null;
  wmtcKmL: number | null;
  /** Official page the displacement came from — the hub's source line. */
  source: string | null;
}

export type ModelSpecRowKey =
  | "displacement"
  | "engine"
  | "transmission"
  | "seatHeight"
  | "curbWeight"
  | "fuelTank"
  | "wmtc";

export interface ModelSpecRow {
  key: ModelSpecRowKey;
  label: string;
  value: string;
}

function toLocale(locale: string | undefined): Locale {
  return (locales as readonly string[]).includes(locale ?? "") ? (locale as Locale) : defaultLocale;
}

function number(value: number, locale: string | undefined): string {
  return new Intl.NumberFormat(toLocale(locale), { maximumFractionDigits: 2 }).format(value);
}

/**
 * Gearbox text with the official gear count, or null when the product's own
 * AT/MT value disagrees (the product row then stays as it is):
 *   product MT + official MT6  → "수동(MT, 6단)"
 *   product AT + official DCT6 → "자동(AT, DCT 6단)"
 *   product AT + official CVT  → "자동(AT)"
 */
export function formatGearbox(
  productTransmission: string | null | undefined,
  gearbox: Gearbox | null,
  t: BikeSpecTranslator,
): string | null {
  if (!gearbox) return null;
  const product = productTransmission?.trim().toUpperCase() || null;
  if (product !== null && product !== "AT" && product !== "MT") return null;
  if (gearbox.kind === "MT") {
    return product === "AT" ? null : t("transmissionMtGears", { gears: gearbox.gears });
  }
  if (product === "MT") return null;
  return gearbox.kind === "DCT"
    ? t("transmissionAtDct", { gears: gearbox.gears })
    : t("transmissionAt");
}

export function formatEngine(
  engine: ModelSpecFacts["engine"],
  t: BikeSpecTranslator,
): string | null {
  if (!engine) return null;
  return t("engineValue", {
    cooling: t(`engineCooling.${engine.cooling}`),
    layout: t(`engineLayout.${engine.layout}`),
  });
}

/**
 * Spec rows in table order. `transmission` is present only when the official
 * gearbox adds to the product's AT/MT (see `formatGearbox`) — the vehicle
 * table swaps it into its own transmission row.
 */
export function formatModelSpecRows(
  facts: ModelSpecFacts | null | undefined,
  productTransmission: string | null | undefined,
  t: BikeSpecTranslator,
  locale?: string,
): ModelSpecRow[] {
  if (!facts) return [];
  const n = (value: number) => number(value, locale);
  const values: Array<[ModelSpecRowKey, string | null]> = [
    ["displacement", facts.displacementCc === null ? null : t("displacementValue", { cc: n(facts.displacementCc) })],
    ["engine", formatEngine(facts.engine, t)],
    ["transmission", formatGearbox(productTransmission, facts.gearbox, t)],
    ["seatHeight", facts.seatHeightMm === null ? null : t("seatHeightValue", { mm: n(facts.seatHeightMm) })],
    ["curbWeight", facts.curbWeightKg === null ? null : t("curbWeightValue", { kg: n(facts.curbWeightKg) })],
    ["fuelTank", facts.fuelTankL === null ? null : t("fuelTankValue", { l: n(facts.fuelTankL) })],
    ["wmtc", facts.wmtcKmL === null ? null : t("wmtcValue", { km: n(facts.wmtcKmL) })],
  ];
  return values
    .filter((entry): entry is [ModelSpecRowKey, string] => entry[1] !== null)
    .map(([key, value]) => ({ key, label: t(`labels.${key}`), value }));
}

/**
 * The vehicle table with official specs: the transmission row gets the gear
 * count (or is added when the product has none), the rest go after the
 * product's own rows.
 */
export function mergeModelSpecRows<Row extends { key: string; label: string; value: string }>(
  rows: readonly Row[],
  specRows: readonly ModelSpecRow[],
): Array<Row | ModelSpecRow> {
  const transmission = specRows.find((row) => row.key === "transmission");
  const rest = specRows.filter((row) => row.key !== "transmission");
  const hasTransmission = rows.some((row) => row.key === "transmission");
  const merged: Array<Row | ModelSpecRow> = rows.map((row) =>
    row.key === "transmission" && transmission ? { ...row, value: transmission.value } : row,
  );
  if (transmission && !hasTransmission) merged.push(transmission);
  return [...merged, ...rest];
}

/**
 * The model hub's two spec lines: "배기량·변속" and (confirmed models only)
 * "시트고·중량". A line without values is null.
 */
export function formatHubSpecLines(
  facts: ModelSpecFacts | null | undefined,
  productTransmission: string | null | undefined,
  t: BikeSpecTranslator,
  locale?: string,
): { sizeGearbox: string | null; seatWeight: string | null; rows: ModelSpecRow[] } {
  const rows = formatModelSpecRows(facts, productTransmission, t, locale).filter(
    (row) => row.key !== "engine" && row.key !== "fuelTank" && row.key !== "wmtc",
  );
  const join = (keys: ModelSpecRowKey[]) => {
    const parts = rows.filter((row) => keys.includes(row.key)).map((row) => row.value);
    return parts.length > 0 ? parts.join(" · ") : null;
  };
  return {
    sizeGearbox: join(["displacement", "transmission"]),
    seatWeight: join(["seatHeight", "curbWeight"]),
    rows,
  };
}
